/** Section 3.3 - the single legality source for the battle kernel.
 *
 * The AI move generator, the UI's affordance highlighting and the property-test
 * fuzzer all read this one function, so its contract is strict: every command it
 * returns must execute `Ok` against the state it was generated from.
 */

import type { BattleCommand } from "../commands.js";
import type { ContentRegistry } from "../content/registry.js";
import {
  BF_HEXES,
  type BattleState, hexCol, inTacticsBand, isAlive, tacticsSide,
} from "./state.js";
import {
  canBatter, canShoot, canTend, isUntargetable, isWarMachine,
} from "./machines.js";
import { F_ACTED, F2_AFRAID, F2_MEDITATED } from "./flags.js";
import { activeStack } from "./order.js";
import { hasAbility } from "./abilities.js";
import { canDevour, isDevourer } from "./devour.js";
import {
  UNREACHABLE, attackPositions, computeReach, meleeBlocked, newOccupancy, canOccupy,
} from "./move.js";
import { enumerateCreatureCasts, enumerateHeroCasts, escapeBarred } from "./spells.js";
import { validateHeroStrike } from "./heroStrike.js";
import { validateRacial } from "./racial.js";
import { validateActivate } from "./activate.js";

export interface LegalFilter {
  readonly kinds?: readonly BattleCommand["type"][];
  readonly limit?: number;
}

export function enumerateLegalBattleCommands(
  state: BattleState, side: 0 | 1, content: ContentRegistry, filter?: LegalFilter,
): readonly BattleCommand[] {
  const out: BattleCommand[] = [];
  const limit = filter?.limit ?? Number.MAX_SAFE_INTEGER;
  const kinds = filter?.kinds;
  const want = (t: BattleCommand["type"]): boolean => kinds === undefined || kinds.includes(t);
  const push = (cmd: BattleCommand): void => { if (out.length < limit && want(cmd.type)) out.push(cmd); };

  if (state.outcome.kind !== "ongoing") return out;
  const st = state.stacks;

  if (state.tacticsPhase) {
    // Only the side that won the tactics contest has anything to do; the other
    // is not offered so much as a DONE, because a phase both sides can end is a
    // phase the loser can end before the winner has placed a stack.
    if (side !== tacticsSide(state)) return out;
    const occ = newOccupancy(state);
    for (let i = 0; i < st.n; i++) {
      if (!isAlive(st, i) || st.side[i] !== side) continue;
      for (let h = 0; h < BF_HEXES; h++) {
        if (!inTacticsBand(state, side, hexCol(h))) continue;
        if (occ[h]! >= 0 && occ[h] !== i) continue;
        if (!canOccupy(state, occ, i, h)) continue;
        push({ type: "BATTLE_TACTICS_MOVE", stack: i, hex: h });
      }
    }
    push({ type: "BATTLE_TACTICS_DONE", side });
    return out;
  }

  const slot = activeStack(state);
  if (slot < 0 || st.side[slot] !== side) return out;
  if ((st.flags[slot]! & F_ACTED) !== 0) return out;

  // Section 27: a war machine is wheeled into position before the battle and
  // stays there. It neither walks nor swings, so neither is enumerated for it.
  if (!isWarMachine(st, slot)) {
    const occ = newOccupancy(state);
    const reach = computeReach(state, slot, occ);
    const here = st.hex[slot]!;

    for (let h = 0; h < BF_HEXES; h++) {
      if (h === here || reach.dist[h] === UNREACHABLE) continue;
      push({ type: "BATTLE_MOVE", stack: slot, hex: h });
    }

    // Fear: the stack is still on the board, still in the way, and will not
    // commit. Moving, defending, waiting and retaliating are all still offered
    // - that is the whole difference between this and Blind, which takes the
    // turn outright.
    const afraid = (st.flags2[slot]! & F2_AFRAID) !== 0;
    const positions: number[] = new Array(BF_HEXES).fill(0);
    // The charged blow doubles this family, which is the largest one here -
    // so it is offered only while the stack can actually pay for it. A creature
    // without the ability, out of uses, or cooling adds nothing at all.
    const charged = st.strikes[slot]! > 0 && st.cooldown[slot]! <= 0
      && hasAbility(content, st.def[slot]!, "MAGIC_ATTACK");
    if (!afraid) {
      for (let t = 0; t < st.n; t++) {
        if (!isAlive(st, t) || st.side[t] === side || isUntargetable(st, t)) continue;
        const k = attackPositions(state, slot, t, reach, positions);
        for (let m = 0; m < k; m++) {
          push({ type: "BATTLE_ATTACK", stack: slot, hex: positions[m]!, target: t });
          if (charged) {
            push({ type: "BATTLE_ATTACK", stack: slot, hex: positions[m]!, target: t, magic: true });
          }
        }
      }
    }
  }

  if ((st.flags2[slot]! & F2_AFRAID) === 0
      && canShoot(st, slot) && st.shots[slot]! > 0 && !meleeBlocked(state, slot)) {
    for (let t = 0; t < st.n; t++) {
      if (!isAlive(st, t) || st.side[t] === side || isUntargetable(st, t)) continue;
      push({ type: "BATTLE_SHOOT", stack: slot, target: t });
    }
  }

  // The two machine commands below are the ones a *crewed* machine issues. No
  // skill crews one yet, so `settle` resolves every machine turn itself and a
  // machine is never the active stack in play; these arms come alive with
  // Ballistics and First Aid, and the legality contract has to describe a state
  // the kernel can represent either way.
  if (canBatter(st, slot) && state.siege !== null && state.siege.active) {
    for (let i = 0; i < state.obstacles.length; i++) {
      const o = state.obstacles[i]!;
      if (o.kind !== "wall" && o.kind !== "gate" && o.kind !== "tower") continue;
      if (o.integrity <= 0) continue;
      push({ type: "BATTLE_CATAPULT", stack: slot, wallSegment: i });
    }
  }

  // The tent mends the top creature of one wounded stack. A stack at full
  // strength is not a legal target, so a tent with nothing to do defends.
  if (canTend(st, slot)) {
    for (let t = 0; t < st.n; t++) {
      if (!isAlive(st, t) || st.side[t] !== side) continue;
      if (st.topHp[t]! >= st.maxHp[t]!) continue;
      push({ type: "BATTLE_HEAL", stack: slot, target: t });
    }
  }

  // Casting is enumerated through the same validator the executor runs, so
  // every command handed back here executes `Ok` by construction.
  if (want("BATTLE_CAST")) for (const cmd of enumerateHeroCasts(state, side, content)) push(cmd);
  if (want("BATTLE_CREATURE_CAST")) {
    for (const cmd of enumerateCreatureCasts(state, slot, content)) push(cmd);
  }

  /**
   * The hero's own two verbs, enumerated through the same validators the
   * executor runs so §3.3's contract holds by construction. Both are gated on
   * `castThisRound`, so a hero is offered the strike, the racial and every cast
   * he could make and may take exactly one of them.
   */
  for (let t = 0; t < st.n; t++) {
    if (validateHeroStrike(state, side, t).ok) push({ type: "BATTLE_HERO_STRIKE", side, target: t });
  }
  if (validateRacial(state, content, side, slot).ok) {
    push({ type: "BATTLE_RACIAL", side, stack: slot });
  }
  if (validateActivate(state, content, slot).ok) push({ type: "BATTLE_ACTIVATE", stack: slot });

  if (state.waited[slot] === 0) push({ type: "BATTLE_WAIT", stack: slot });
  push({ type: "BATTLE_DEFEND", stack: slot });
  // Meditation, offered on exactly the two conditions the executor checks: the
  // creature has the ability and has not already spent it this battle. A
  // command the kernel implements and nothing enumerates is one the AI never
  // issues and no screen can show - which is what happened to this one until
  // another session pointed it out.
  // Devour: every corpse still on the field that this stack may eat. Offered
  // per corpse rather than as one command with a chosen target, so the choice
  // is a command like every other and the AI can price each meal.
  if (isDevourer(content, state, slot)) {
    for (let i = 0; i < state.stacks.n; i++) {
      if (canDevour(content, state, slot, i)) {
        push({ type: "BATTLE_DEVOUR", stack: slot, target: i });
      }
    }
  }
  // Heat Stroke: always available to a machine that has it, because unlike an
  // attack it needs no reachable target - venting into an empty fan is legal
  // and simply wastes the turn, which is the player's call to make.
  if (hasAbility(content, state.stacks.def[slot]!, "HEAT_STROKE")) {
    push({ type: "BATTLE_HEAT_STROKE", stack: slot });
  }
  if (hasAbility(content, state.stacks.def[slot]!, "MEDITATION")
    && (state.stacks.flags2[slot]! & F2_MEDITATED) === 0) {
    push({ type: "BATTLE_MEDITATE", stack: slot });
  }

  // Chains of Battle hold both heroes, so neither door is even offered.
  if (state.heroes[side].id !== null && !escapeBarred(state)) {
    push({ type: "BATTLE_RETREAT", side });
    if (state.heroes[1 - side]!.id !== null) push({ type: "BATTLE_SURRENDER", side });
  }

  return out;
}
